/** Тонкая обёртка над window.Telegram.WebApp для мини-аппа.
 *
 * Вне Telegram (обычный браузер, локальная разработка) WebApp просто
 * отсутствует — все вызовы здесь безопасно превращаются в no-op.
 */

type TelegramWebApp = {
  initData?: string;
  initDataUnsafe?: { start_param?: string };
  platform?: string;
  ready?: () => void;
  expand?: () => void;
  requestFullscreen?: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
  addToHomeScreen?: () => void;
  checkHomeScreenStatus?: (callback: (status: string) => void) => void;
  onEvent?: (event: string, callback: () => void) => void;
  offEvent?: (event: string, callback: () => void) => void;
};

function webApp(): TelegramWebApp | undefined {
  return typeof window !== 'undefined'
    ? (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp
    : undefined;
}

// WebApp.platform: "android"/"android_x"/"ios" — телефон, где фулскрин и
// нужен (там же прячется собственная шапка Telegram). "tdesktop"/"macos"/
// "weba"/"webk" — десктопные и веб-клиенты с окном фиксированного размера,
// где фулскрин Мини-аппу не идёт — оставляем как есть.
const MOBILE_PLATFORMS = new Set(['android', 'android_x', 'ios']);

/** Готовим мини-апп: разворачиваем на весь экран, сообщаем Telegram, что готовы.
 * requestFullscreen — Bot API 8.0+ (растягивает поверх шапки Telegram, а не
 * только до неё, как expand); в старых клиентах такого метода просто нет.
 *
 * Отступы под системную шапку и под плавающую кнопку «Закрыть» Telegram
 * сам пишет как CSS-переменные --tg-safe-area-inset-* и
 * --tg-content-safe-area-inset-* на :root и держит их актуальными через
 * нативные события — трогать их из JS не нужно, см. использование в App.tsx. */
export function setupTelegram() {
  const app = webApp();
  if (!app) return;
  app.ready?.();
  app.expand?.();
  if (MOBILE_PLATFORMS.has(app.platform || '')) {
    try {
      app.requestFullscreen?.();
    } catch {
      /* старые клиенты Telegram не знают этот метод */
    }
  }
  try {
    app.disableVerticalSwipes?.();
  } catch {
    /* старые клиенты Telegram не знают этот метод */
  }
  try {
    app.setHeaderColor?.('#03110D');
    app.setBackgroundColor?.('#03110D');
  } catch {
    /* недоступно в вебе */
  }
}

// --- Иконка на экране «Домой» (пока только iOS) ----------------------------

export const BOT_USERNAME = 'housepoker_bot' as const;
/** Открыть мини-апп в Telegram (основное мини-приложение бота). */
export const TELEGRAM_APP_LINK = `https://t.me/${BOT_USERNAME}?startapp` as const;

/** Параметр из ссылки t.me/<бот>?startapp=<значение>. */
export function telegramStartParam(): string | null {
  const app = webApp();
  const unsafe = app?.initDataUnsafe?.start_param?.trim();
  if (unsafe) return unsafe;
  const signed = app?.initData ? new URLSearchParams(app.initData).get('start_param')?.trim() : '';
  if (signed) return signed;
  // Fallback полезен в Telegram Web и для локальной проверки ссылок.
  const query = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  return query?.get('tgWebAppStartParam')?.trim() || query?.get('startapp')?.trim() || null;
}

export const telegramSectionLink = (destination: string) =>
  `${TELEGRAM_APP_LINK}=${encodeURIComponent(destination)}`;

// Только для разработки: ?ios-safari / ?ios-standalone в адресе изображают
// iPhone в Safari — в браузере на компьютере по-другому этот режим не увидеть.
const devFlag = (name: string) =>
  process.env.NODE_ENV === 'development' && new URLSearchParams(window.location.search).has(name);

/** Где открыт мини-апп на iPhone/iPad: в Telegram, в Safari (как сайт) или не на iOS. */
export function iosContext(): 'telegram' | 'safari' | null {
  const app = webApp();
  if (app?.platform === 'ios') return 'telegram';
  if (app?.initData) return null; // Telegram, но не iOS
  if (devFlag('ios-safari') || devFlag('ios-standalone')) return 'safari';
  const ua = navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  return iOS ? 'safari' : null;
}

/** Сайт открыт с иконки на экране «Домой» (для Safari). */
export function isStandalone(): boolean {
  if (devFlag('ios-standalone')) return true;
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true
  );
}

/** 'added' — иконка уже есть; 'available' — Telegram умеет добавить её сам
 * (Bot API 8.0+); 'manual' — только вручную, по инструкции. */
export type HomeScreenSupport = 'added' | 'available' | 'manual';

export function homeScreenSupport(): Promise<HomeScreenSupport> {
  const app = webApp();
  if (!app?.addToHomeScreen || !app.checkHomeScreenStatus || !app.isVersionAtLeast?.('8.0')) {
    return Promise.resolve('manual');
  }
  return new Promise((resolve) => {
    // Если клиент так и не ответил на проверку — считаем, что добавить можно
    const timer = setTimeout(() => resolve('available'), 1500);
    try {
      app.checkHomeScreenStatus!((status) => {
        clearTimeout(timer);
        resolve(status === 'added' ? 'added' : status === 'unsupported' ? 'manual' : 'available');
      });
    } catch {
      clearTimeout(timer);
      resolve('manual');
    }
  });
}

/** Системное предложение Telegram добавить мини-апп на экран «Домой».
 * onAdded вызовется, когда пользователь подтвердит добавление. */
export function addToHomeScreen(onAdded: () => void): boolean {
  const app = webApp();
  if (!app?.addToHomeScreen) return false;
  const handler = () => {
    app.offEvent?.('homeScreenAdded', handler);
    onAdded();
  };
  app.onEvent?.('homeScreenAdded', handler);
  try {
    app.addToHomeScreen();
    return true;
  } catch {
    app.offEvent?.('homeScreenAdded', handler);
    return false;
  }
}
